import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { getReviewLoginEnabled, reviewLogin } from "@/api/auth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { appConfig, setAccessToken } from "@/config";
import { useLanguage } from "@/contexts/language";
import { loadSession } from "@/session";

export function LoginPage() {
  const { t, dir } = useLanguage();
  const navigate = useNavigate();

  // Store-review bypass: a discreet entry that trades the secret code (given
  // to app reviewers in the store console) for a demo-account session. The
  // API refuses everything unless REVIEW_LOGIN_CODE is configured.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewEmail, setReviewEmail] = useState("");
  const [reviewCode, setReviewCode] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  // The entry link only renders when the server says the bypass is
  // configured — with REVIEW_LOGIN_CODE unset it never appears.
  const [reviewEnabled, setReviewEnabled] = useState(false);
  useEffect(() => {
    getReviewLoginEnabled()
      .then((result) => setReviewEnabled(result.enabled))
      .catch(() => setReviewEnabled(false));
  }, []);

  const signIn = () => {
    const callbackUrl = `${window.location.origin}/auth/callback`;
    window.location.assign(
      `https://bawabah.app/login?appId=${appConfig.bawabahAppId}&callbackUrl=${encodeURIComponent(callbackUrl)}&fullscreen=true`,
    );
  };

  const submitReview = async () => {
    const email = reviewEmail.trim();
    const code = reviewCode.trim();
    if (!email || !code || reviewBusy) return;
    setReviewBusy(true);
    try {
      const result = await reviewLogin({ email, code });
      setAccessToken(result.accessToken);
      await loadSession();
      void navigate({ to: "/home", replace: true });
    } catch {
      setAccessToken(null);
      toast.error(t("Mandoob:Login.ReviewFailed"));
    } finally {
      setReviewBusy(false);
    }
  };

  return (
    <div
      dir={dir}
      className="tw:flex tw:min-h-dvh tw:flex-col tw:items-center tw:justify-center tw:gap-8 tw:bg-background tw:p-8 tw:pb-[calc(32px+env(safe-area-inset-bottom))] tw:pt-[calc(32px+env(safe-area-inset-top))]"
    >
      <div className="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:text-center">
        <img src="/logo.svg" alt="" className="tw:size-24" />
        <h1 className="tw:m-0 tw:text-3xl tw:font-bold tw:text-foreground">
          {t("Mandoob:AppName")}
        </h1>
        <p className="tw:m-0 tw:max-w-xs tw:text-base tw:text-muted-foreground">
          {t("Mandoob:Login.Tagline")}
        </p>
      </div>
      <Button size="lg" onClick={signIn} className="tw:w-full tw:max-w-xs">
        {t("Mandoob:Login.SignIn")}
      </Button>

      {/* Deliberately quiet — and gone entirely when the bypass is off. */}
      {reviewEnabled ? (
        <button
          type="button"
          onClick={() => setReviewOpen(true)}
          className="tw:text-xs tw:text-muted-foreground/60 tw:underline-offset-2 tw:hover:underline"
        >
          {t("Mandoob:Login.ReviewEntry")}
        </button>
      ) : null}

      <Dialog
        open={reviewOpen}
        onOpenChange={(next) => !next && setReviewOpen(false)}
      >
        <DialogContent dir={dir} className="tw:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("Mandoob:Login.ReviewTitle")}</DialogTitle>
          </DialogHeader>
          <Input
            type="email"
            dir="ltr"
            value={reviewEmail}
            onChange={(event) => setReviewEmail(event.target.value)}
            placeholder={t("Mandoob:Login.ReviewEmailPlaceholder")}
            autoFocus
          />
          <Input
            dir="ltr"
            value={reviewCode}
            onChange={(event) => setReviewCode(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void submitReview();
            }}
            placeholder={t("Mandoob:Login.ReviewCodePlaceholder")}
          />
          <Button
            type="button"
            onClick={() => void submitReview()}
            loading={reviewBusy}
            disabled={!reviewEmail.trim() || !reviewCode.trim()}
            className="tw:w-full"
          >
            {t("Mandoob:Login.SignIn")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
